/** 点选改页：内联芯片 + 文字混排（DOM 解析 / 剪贴板） */

import {
  pickRefAlias,
  pickRefChipDisplay,
  pickRefChipLabel,
  pickRefId,
  PICK_BUNDLE_MARKER_END,
  PICK_BUNDLE_MARKER_START,
  type PickRef,
  type PickRefBundle,
  type PickRefPageContext,
} from "./brand-site-pick-refs";

export type PickRefSegment =
  | { type: "text"; text: string }
  | { type: "pick"; pick: PickRef; index: number };

export type PickRefComposerContent = PickRefPageContext & {
  segments: PickRefSegment[];
  picks: PickRef[];
  userRequest: string;
};

const PICK_DATA_ATTR = "data-pick-ref";
const PICK_JSON_ATTR = "data-pick-json";

export function pickRefChipClassName(): string {
  return "mx-0.5 inline-flex max-w-full align-middle items-center gap-0.5 rounded-md border border-primary/50 bg-primary px-1.5 py-0 text-[11px] font-medium leading-snug text-primary-foreground shadow-sm";
}

function encodePickJson(pick: PickRef): string {
  return encodeURIComponent(JSON.stringify(pick));
}

function decodePickJson(raw: string | null | undefined): PickRef | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as PickRef;
    if (!parsed?.path) return null;
    return {
      id: pickRefId(String(parsed.path)),
      tag: String(parsed.tag ?? "div"),
      text: String(parsed.text ?? ""),
      path: String(parsed.path),
      html: String(parsed.html ?? ""),
    };
  } catch {
    return null;
  }
}

export function createPickChipElement(
  pick: PickRef,
  options?: { removable?: boolean; index?: number },
): HTMLSpanElement {
  const index = options?.index ?? 0;
  const chip = document.createElement("span");
  chip.setAttribute(PICK_DATA_ATTR, "1");
  chip.setAttribute(PICK_JSON_ATTR, encodePickJson(pick));
  chip.setAttribute("data-pick-alias", pickRefAlias(index));
  chip.setAttribute("data-pick-path", pick.path);
  chip.setAttribute("contenteditable", "false");
  chip.setAttribute("title", pickRefChipLabel(pick));
  chip.className = pickRefChipClassName();

  const label = document.createElement("span");
  label.className = "min-w-0 truncate font-mono text-[11px] tabular-nums";
  label.textContent = pickRefChipDisplay(index);
  chip.appendChild(label);

  if (options?.removable) {
    const remove = document.createElement("button");
    remove.type = "button";
    remove.setAttribute("aria-label", "移除选区");
    remove.className =
      "shrink-0 rounded p-0.5 leading-none hover:bg-primary-foreground/15";
    remove.textContent = "×";
    remove.addEventListener("mousedown", (event) => event.preventDefault());
    remove.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const parent = chip.parentElement;
      chip.remove();
      if (parent) refreshPickChipAliases(parent);
      chip.ownerDocument?.defaultView?.getSelection()?.collapse(parent ?? chip, 0);
      parent?.dispatchEvent(new InputEvent("input", { bubbles: true }));
    });
    chip.appendChild(remove);
  }

  return chip;
}

/** 按首次出现顺序重编号芯片为 @1 @2… */
export function refreshPickChipAliases(root: HTMLElement) {
  const seen = new Map<string, number>();
  root.querySelectorAll<HTMLElement>(`[${PICK_DATA_ATTR}]`).forEach((chip) => {
    const pick = decodePickJson(chip.getAttribute(PICK_JSON_ATTR));
    if (!pick) return;
    let index = seen.get(pick.id);
    if (index == null) {
      index = seen.size;
      seen.set(pick.id, index);
    }
    const alias = pickRefAlias(index);
    chip.setAttribute("data-pick-alias", alias);
    chip.setAttribute("data-pick-path", pick.path);
    chip.setAttribute("title", pickRefChipLabel(pick));
    const label = chip.querySelector("span");
    if (label) label.textContent = pickRefChipDisplay(index);
  });
}

export function parsePickRefEditorRoot(root: HTMLElement): PickRefComposerContent | null {
  const pageSlug = root.dataset.pageSlug ?? "";
  const pagePath = root.dataset.pagePath ?? "";
  const pageTitle = root.dataset.pageTitle || null;

  const segments: PickRefSegment[] = [];
  const picks: PickRef[] = [];
  const pickIndex = new Map<string, number>();

  function pushText(text: string) {
    const normalized = text.replace(/\u00a0/g, " ");
    if (!normalized) return;
    const last = segments[segments.length - 1];
    if (last?.type === "text") {
      last.text += normalized;
      return;
    }
    segments.push({ type: "text", text: normalized });
  }

  function pushPick(pick: PickRef) {
    let index = pickIndex.get(pick.id);
    if (index == null) {
      index = picks.length;
      picks.push(pick);
      pickIndex.set(pick.id, index);
    }
    segments.push({ type: "pick", pick, index });
  }

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(node.textContent ?? "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.hasAttribute(PICK_DATA_ATTR)) {
      const pick = decodePickJson(el.getAttribute(PICK_JSON_ATTR));
      if (pick) pushPick(pick);
      return;
    }
    if (el.tagName === "BR") {
      pushText("\n");
      return;
    }
    for (const child of el.childNodes) walk(child);
  }

  for (const child of root.childNodes) walk(child);

  const userRequest = segmentsToUserRequest(segments);

  if (!userRequest && picks.length === 0) return null;

  if (!pageSlug) {
    return {
      pageSlug: "",
      pageTitle: null,
      pagePath: "",
      segments,
      picks,
      userRequest,
    };
  }

  return {
    pageSlug,
    pageTitle,
    pagePath,
    segments,
    picks,
    userRequest,
  };
}

export function segmentsToUserRequest(segments: PickRefSegment[]): string {
  return segments
    .map((segment) => {
      if (segment.type === "text") return segment.text;
      return pickRefAlias(segment.index);
    })
    .join("")
    .replace(/\s+/g, (match) => (match.includes("\n") ? match : " "))
    .trim();
}

type ClipboardPayload = PickRefBundle & { segments?: PickRefSegment[] };

function encodePayload(payload: ClipboardPayload): string {
  const json = JSON.stringify(payload);
  if (typeof btoa === "function") {
    return btoa(unescape(encodeURIComponent(json)));
  }
  return Buffer.from(json, "utf8").toString("base64");
}

function decodePayload(encoded: string): ClipboardPayload | null {
  try {
    const json =
      typeof atob === "function"
        ? decodeURIComponent(escape(atob(encoded)))
        : Buffer.from(encoded, "base64").toString("utf8");
    const parsed = JSON.parse(json) as ClipboardPayload;
    if (!parsed?.pageSlug || !Array.isArray(parsed.picks) || parsed.picks.length === 0) return null;
    return {
      pageSlug: String(parsed.pageSlug),
      pageTitle: parsed.pageTitle ?? null,
      pagePath: String(parsed.pagePath ?? ""),
      picks: parsed.picks.map((pick) => ({
        id: pickRefId(String(pick.path ?? pick.id ?? "")),
        tag: String(pick.tag ?? "div"),
        text: String(pick.text ?? ""),
        path: String(pick.path ?? ""),
        html: String(pick.html ?? ""),
      })),
      segments: parsed.segments,
    };
  } catch {
    return null;
  }
}

/** 复制混排内容：HTML 保留芯片 + 隐藏 bundle（含 segments 顺序）。 */
export function serializePickRefComposerForClipboard(content: PickRefComposerContent): {
  text: string;
  html: string;
} {
  const payload: ClipboardPayload = {
    pageSlug: content.pageSlug,
    pageTitle: content.pageTitle,
    pagePath: content.pagePath,
    picks: content.picks,
    segments: content.segments,
  };
  const marker = `${PICK_BUNDLE_MARKER_START}${encodePayload(payload)}${PICK_BUNDLE_MARKER_END}`;
  const reading =
    segmentsToUserRequest(content.segments) ||
    content.picks.map((_, index) => pickRefAlias(index)).join(" ");
  return {
    text: `${marker}${reading}`,
    html: `${marker}${composerToHtml(content)}`,
  };
}

export function composerToHtml(content: PickRefComposerContent): string {
  return content.segments
    .map((segment) => {
      if (segment.type === "text") {
        return segment.text
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>");
      }
      return createPickChipElement(segment.pick, { index: segment.index }).outerHTML;
    })
    .join("");
}

/** 去掉粘贴时浏览器插入的块级包裹层，避免芯片上方出现大空隙。 */
export function normalizePickRefEditorRoot(root: HTMLElement) {
  root.querySelectorAll("[data-pick-composer]").forEach((wrapper) => {
    const parent = wrapper.parentNode;
    if (!parent) return;
    while (wrapper.firstChild) parent.insertBefore(wrapper.firstChild, wrapper);
    parent.removeChild(wrapper);
  });

  while (root.firstChild) {
    const first = root.firstChild;
    if (first.nodeType === Node.TEXT_NODE && !first.textContent?.replace(/\u00a0/g, " ").trim()) {
      root.removeChild(first);
      continue;
    }
    if (first.nodeType === Node.ELEMENT_NODE) {
      const el = first as HTMLElement;
      if (el.tagName === "BR") {
        root.removeChild(first);
        continue;
      }
      if (
        el.tagName === "DIV" &&
        !el.hasAttribute(PICK_DATA_ATTR) &&
        !el.querySelector(`[${PICK_DATA_ATTR}]`) &&
        !el.textContent?.replace(/\u00a0/g, " ").trim()
      ) {
        root.removeChild(first);
        continue;
      }
    }
    break;
  }

  while (
    root.childNodes.length === 1 &&
    root.firstChild?.nodeType === Node.ELEMENT_NODE &&
    (root.firstChild as HTMLElement).tagName === "DIV" &&
    !(root.firstChild as HTMLElement).hasAttribute(PICK_DATA_ATTR)
  ) {
    const div = root.firstChild as HTMLElement;
    while (div.firstChild) root.insertBefore(div.firstChild, div);
    div.remove();
  }

  refreshPickChipAliases(root);
}

export function parsePickRefComposerFromClipboard(text: string, html?: string): PickRefComposerContent | null {
  const trimmed = text.trimStart();
  if (trimmed.startsWith(PICK_BUNDLE_MARKER_START)) {
    const end = trimmed.indexOf(PICK_BUNDLE_MARKER_END);
    if (end > 0) {
      const payload = decodePayload(trimmed.slice(PICK_BUNDLE_MARKER_START.length, end));
      if (payload) {
        const segments =
          payload.segments ??
          payload.picks.map((pick, index) => ({ type: "pick" as const, pick, index }));
        return {
          pageSlug: payload.pageSlug,
          pageTitle: payload.pageTitle,
          pagePath: payload.pagePath,
          segments,
          picks: payload.picks,
          userRequest: segmentsToUserRequest(segments),
        };
      }
    }
  }

  if (html && html.includes(PICK_DATA_ATTR)) {
    const template = document.createElement("div");
    template.innerHTML = html;
    const root =
      template.querySelector<HTMLElement>("[data-pick-composer]") ?? template;
    normalizePickRefEditorRoot(root);
    const payload = payloadFromMarker(trimmed);
    if (payload && !root.dataset.pageSlug) {
      root.dataset.pageSlug = payload.pageSlug;
      root.dataset.pagePath = payload.pagePath;
      root.dataset.pageTitle = payload.pageTitle ?? "";
    }
    return parsePickRefEditorRoot(root);
  }

  return null;
}

function payloadFromMarker(text: string): ClipboardPayload | null {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith(PICK_BUNDLE_MARKER_START)) return null;
  const end = trimmed.indexOf(PICK_BUNDLE_MARKER_END);
  if (end < 0) return null;
  return decodePayload(trimmed.slice(PICK_BUNDLE_MARKER_START.length, end));
}

export function applyComposerToEditorRoot(root: HTMLElement, content: PickRefComposerContent) {
  root.dataset.pageSlug = content.pageSlug;
  root.dataset.pagePath = content.pagePath;
  root.dataset.pageTitle = content.pageTitle ?? "";
  root.innerHTML = "";
  for (const segment of content.segments) {
    if (segment.type === "text") {
      root.appendChild(document.createTextNode(segment.text));
      continue;
    }
    root.appendChild(
      createPickChipElement(segment.pick, { removable: true, index: segment.index }),
    );
  }
  normalizePickRefEditorRoot(root);
  refreshPickChipAliases(root);
}

export function insertPickAtSelection(root: HTMLElement, pick: PickRef) {
  const existing = parsePickRefEditorRoot(root);
  const known = existing?.picks.findIndex((item) => item.id === pick.id) ?? -1;
  const nextIndex =
    known >= 0 ? known : (existing?.picks.length ?? root.querySelectorAll(`[${PICK_DATA_ATTR}]`).length);
  const chip = createPickChipElement(pick, { removable: true, index: nextIndex });
  const space = document.createTextNode("\u00a0");
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0 && root.contains(selection.anchorNode)) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(space);
    range.insertNode(chip);
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    root.appendChild(chip);
    root.appendChild(space);
  }
  refreshPickChipAliases(root);
}
